import { GameFlowBase } from './game-flow-base';
import { gameViewData } from './game-view-data';
import { GameConfig } from './models/game-config.model';
import { CardState, GameViewModel, HandModel, HandState, SideModel, SideState } from './models/game-view.model';
import { AppPhase, DealingSubPhase } from '../models/communication/app-enums.model';
import { CardDealtEvent } from '../models/communication/app-events.model';
import { Card } from '../models/communication/cards.enum';
import { CardPosition, Outcome, SidePosition } from '../models/communication/games.model';

/**
 * An abstract class for all the main game types (`baccarat-game`, `dragon-tiger-game`…)
 *
 * The main game types extend the class with additional functionality
 * and provide `GameConfig` that defines `GameViewModel`.
 *
 * The property `gameFlow` allows composing different game variants (flows) with the same sub classes,
 * (`open-game-flow`, `classic-game-flow`…)
 *
 * The class provides the API for manipulating view model data.
 * A set of high level helper methods define common actions needed at different game states,
 *
 * Finally, the abstract methods need to be implemented by the sub classes.
 */
export abstract class GameBase {
    readonly data!: GameViewModel;
    protected readonly gameFlow!: GameFlowBase<GameBase>;

    constructor(config: GameConfig) {
        this.data = gameViewData.getDataFromConfig(config);
    }

    onChanges(table?: CardDealtEvent, subPhase?: DealingSubPhase, appPhase?: AppPhase): void {
        this.gameFlow.onChanges(table, subPhase, appPhase);
    }

    // Title

    updateTitle(text: string, params: { isSpecial: boolean } = { isSpecial: false }): void {
        this.data.title.text = text;
        this.data.title.isSpecial = params.isSpecial;
    }

    // Cards

    getCardState(position: CardPosition): CardState {
        return this.data.cards[position].state;
    }

    getCardParentHand(cardPosition: CardPosition): HandModel {
        return this.data.cards[cardPosition].parentHand;
    }

    getCardParentSide(cardPosition: CardPosition): SideModel {
        return this.data.cards[cardPosition].parentHand.parentSide;
    }

    showCard(position: CardPosition, value: Card): void {
        this.updateCard(position, CardState.Show, value);
    }

    updateCard(position: CardPosition, state: CardState, value: Card = Card.Unknown): void {
        const card = this.data.cards[position];
        card.state = state;
        card.value = value;
    }

    updateVisibleCards(state: CardState): void {
        for (const card of this.data.cards) {
            if (card.state !== CardState.Hidden) {
                this.updateCard(card.position, state);
            }
        }
    }

    // Sides

    getSide(sidePosition: SidePosition): SideModel {
        const finalSide = this.data.sides.find((side) => side.position === sidePosition);
        if (!finalSide) throw new Error('side must be defined!');
        return finalSide;
    }

    focusOnCardParentSide(cardPosition: CardPosition): void {
        this.updateSides(SideState.Muted);
        this.updateSide(this.getCardParentSide(cardPosition).position, SideState.Current);
    }

    updateSides(state: SideState): void {
        for (const side of this.data.sides) {
            this.updateSide(side.position, state);
        }
    }

    updateSide(sidePosition: SidePosition, state: SideState): void {
        for (const side of this.data.sides) {
            if (side.position === sidePosition) {
                side.state = state;
            }
        }
    }

    // Hands

    getFirstHandInSide(sidePosition: SidePosition): HandModel {
        return this.getSide(sidePosition).hands[0];
    }

    updateHands(state: HandState): void {
        for (const hand of this.data.hands) {
            this.updateHand(hand, state);
        }
    }

    updateHand(hand: HandModel, state: HandState): void {
        // eslint-disable-next-line no-param-reassign
        hand.state = state;
    }

    // Helper methods

    revealCard(cardPosition: CardPosition, card: Card): void {
        this.showCard(cardPosition, card);
        const wordCard = this.getWordCard({ lowerCase: false, plural: this.getCardParentHand(cardPosition).cards.length > 1 });
        this.updateTitle($localize`${wordCard} flipped`, { isSpecial: true });
        this.focusOnCardParentSide(cardPosition);
    }

    getWordCard(params: { plural: boolean; lowerCase: boolean } = { plural: false, lowerCase: true }): string {
        let word = params.plural ? $localize`Cards` : $localize`Card`;
        if (params.lowerCase) {
            word = word.toLocaleLowerCase();
        }
        return word;
    }

    showHandValue(table: CardDealtEvent): void {
        const hand = this.getCardParentHand(table.cardPosition);

        hand.amount.visible = true;

        if (hand.amountKey !== undefined) {
            hand.amount.value = (table[hand.amountKey as keyof CardDealtEvent] as number) || 0;
        }
    }

    faceUpMessage(cardPosition: CardPosition): void {
        this.updateCard(cardPosition, CardState.MessageFaceUp);
        this.updateTitle($localize`Draw face up card and place card on ${this.getFieldLabel(cardPosition)}`);
        this.focusOnCardParentSide(cardPosition);
    }

    faceDownMessage(cardPosition: CardPosition): void {
        this.updateCard(cardPosition, CardState.MessageFaceDown);
        this.updateTitle($localize`Draw face down card and place card on ${this.getFieldLabel(cardPosition)}`);
        this.focusOnCardParentSide(cardPosition);
    }

    playerDecisionTime(): void {
        this.updateTitle($localize`Player decision time`, { isSpecial: true });
    }

    allCardsDrawn(): void {
        this.updateTitle($localize`All cards have been drawn`, { isSpecial: true });
        this.updateSides(SideState.Current);
    }

    // Outcome

    handleOutcome(table: Pick<CardDealtEvent, 'outcome' | 'outcomes'>): void {
        if (table.outcome !== undefined) {
            this.updateTitle(this.getOutcomeLabel(table.outcome));
            this.updateSides(SideState.Default);
            this.setOutcomeStates(table.outcome, table.outcomes);
        }
    }

    // Abstract methods

    abstract setOutcomeStates(outcome: Outcome, outcomes?: Outcome[]): void;
    abstract getFirstCardPosition(): CardPosition;
    abstract getFieldLabel(cardPosition: CardPosition): string;
    abstract getOutcomeLabel(outcome: Outcome): string;
}
